import React, { useEffect, useState } from 'react';
import { 
    FaSearch, FaUserPlus, FaEdit, FaTrash, FaSave, 
    FaUsers, FaHistory, FaFileInvoiceDollar 
} from 'react-icons/fa';
import { 
    fetchClients, createClient, updateClient, deleteClient, exportClientRedevancePDF 
} from '../api';

const initialForm = { 
    code: '', name: '', address: '', contact: '', email: '', 
    initialDebt: '', ccNumber: '', rccmNumber: '' 
};

const ClientPage = ({ user }) => {
    const [clients, setClients] = useState([]);
    const [form, setForm] = useState(initialForm);
    const [editingId, setEditingId] = useState(null);
    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState(null);
    const [search, setSearch] = useState("");

    const load = async () => {
        setLoading(true);
        try { 
            const res = await fetchClients(); 
            setClients(res.data); 
        } catch (err) { 
            setMessage({ type: 'error', text: 'Impossible de charger les clients' }); 
        } finally { 
            setLoading(false); 
        }
    };

    useEffect(() => { load(); }, []);

    const filteredClients = clients.filter(c => 
        c.name.toLowerCase().includes(search.toLowerCase()) ||
        c.code.toLowerCase().includes(search.toLowerCase())
    );

    const handleChange = (e) => {
        const { name, value } = e.target;
        setForm(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!form.code || !form.name || !form.contact || !form.address) {
            setMessage({ type: 'warning', text: 'Veuillez remplir les champs obligatoires (*)' }); 
            return;
        }

        const payload = { ...form, initialDebt: Number(form.initialDebt) || 0 };

        try {
            if (editingId) {
                await updateClient(editingId, payload);
                setMessage({ type: 'success', text: '✅ Client mis à jour' });
            } else {
                await createClient(payload);
                setMessage({ type: 'success', text: '✅ Client créé avec succès' });
            }
            setForm(initialForm); 
            setEditingId(null); 
            await load();
            setTimeout(() => setMessage(null), 3000);
        } catch (err) {
            const errorMsg = err.response?.data?.message || "Erreur de doublon ou réseau.";
            setMessage({ type: 'error', text: `❌ ${errorMsg}` });
        }
    };

    const startEdit = (c) => {
        setEditingId(c._id);
        setForm({
            code: c.code, name: c.name, address: c.address, contact: c.contact,
            email: c.email || '', initialDebt: c.initialDebt || '',
            ccNumber: c.ccNumber || '', rccmNumber: c.rccmNumber || ''
        });
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const handleDelete = async (id) => {
        if (user?.role !== 'admin') return;
        if (!window.confirm('Supprimer définitivement ce client ?')) return;
        try {
            await deleteClient(id);
            setMessage({ type: 'success', text: '🗑️ Client supprimé' });
            await load();
        } catch (err) { setMessage({ type: 'error', text: 'Erreur suppression' }); }
    };

    const handleDownloadRedevance = async (clientId, clientName) => {
        try {
            const res = await exportClientRedevancePDF(clientId);
            const url = window.URL.createObjectURL(new Blob([res.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `Redevance_${clientName}.pdf`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            setMessage({ type: 'success', text: 'État de redevance généré !' });
        } catch (e) { setMessage({ type: 'error', text: 'Erreur génération redevance.' }); }
    };

    return (
        <div className="container-fluid py-4 px-3 px-md-4">
            
            {/* HEADER SECTION */}
            <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center mb-4 gap-2 text-center text-md-start">
                <div>
                    <h4 className="fw-bold mb-0 text-uppercase text-dark" style={{ letterSpacing: '1px' }}>
                        <FaUsers className="me-2 text-success"/> BASE CLIENTS
                    </h4>
                    <p className="small mb-0 mt-1 text-muted" style={{ letterSpacing: '1px' }}>Gestion du portefeuille et des reports de soldes</p>
                </div>
            </div>

            {/* MESSAGES D'ALERTE */}
            {message && (
                <div className={`alert py-2 shadow-sm border-0 ${message.type === 'error' ? 'alert-danger' : (message.type === 'warning' ? 'alert-warning' : 'alert-success')}`}>
                    {message.text}
                </div>
            )}

            {/* FORMULAIRE SECTION */}
            <div className="card shadow-sm border-0 mb-4">
                <div className="card-header bg-white py-3 fw-bold text-uppercase text-success" style={{ letterSpacing: '1px' }}>
                    {editingId ? "MODIFIER LES INFORMATIONS" : "NOUVEAU CLIENT"}
                </div>
                <div className="card-body p-4">
                    <form onSubmit={handleSubmit}>
                        <div className="row g-3">
                            <div className="col-12 col-sm-6 col-md-2">
                                <label className="fw-bold small mb-1 text-muted">CODE *</label>
                                <input name="code" value={form.code} onChange={handleChange} className="form-control form-control-sm" placeholder="ex: xxx" />
                            </div>
                            <div className="col-12 col-sm-6 col-md-5">
                                <label className="fw-bold small mb-1 text-muted">NOM DU CLIENT / STRUCTURE *</label>
                                <input name="name" value={form.name} onChange={handleChange} className="form-control form-control-sm" placeholder="Nom complet" />
                            </div>
                            <div className="col-12 col-sm-6 col-md-5">
                                <label className="fw-bold small mb-1 text-muted">CONTACT TÉLÉPHONIQUE *</label>
                                <input name="contact" value={form.contact} onChange={handleChange} className="form-control form-control-sm" placeholder="00 00 00 00 00" />
                            </div>

                            <div className="col-12 col-sm-6 col-md-3">
                                <label className="fw-bold small mb-1 text-muted">ADRESSE EMAIL</label>
                                <input name="email" value={form.email} onChange={handleChange} className="form-control form-control-sm" placeholder="client@email.com" />
                            </div>
                            <div className="col-12 col-md-5">
                                <label className="fw-bold small mb-1 text-muted">ADRESSE GÉOGRAPHIQUE *</label>
                                <input name="address" value={form.address} onChange={handleChange} className="form-control form-control-sm" placeholder="Ville, Commune, Quartier" />
                            </div>
                            <div className="col-12 col-sm-6 col-md-2">
                                <label className="fw-bold small mb-1 text-muted">N° CC</label>
                                <input name="ccNumber" value={form.ccNumber} onChange={handleChange} className="form-control form-control-sm" placeholder="Compte Contrib." />
                            </div>
                            <div className="col-12 col-sm-6 col-md-2">
                                <label className="fw-bold small mb-1 text-muted">N° RCCM</label>
                                <input name="rccmNumber" value={form.rccmNumber} onChange={handleChange} className="form-control form-control-sm" placeholder="RCCM" />
                            </div>

                            <div className="col-12 col-md-4 mt-4">
                                <div className="p-3 border rounded bg-light border-danger-subtle">
                                    <label className="fw-bold small mb-2 text-danger"><FaHistory className="me-2"/>SOLDE INITIAL (Report de dette)</label>
                                    <div className="input-group input-group-sm">
                                        <input 
                                            type="number" name="initialDebt" value={form.initialDebt} onChange={handleChange} 
                                            className="form-control fw-bold text-danger" placeholder="0" 
                                        />
                                        <span className="input-group-text bg-white text-muted">FCFA</span>
                                    </div>
                                </div>
                            </div>

                            <div className="col-12 mt-4 d-flex justify-content-end pt-3 border-top">
                                {editingId && (
                                    <button type="button" className="btn btn-sm btn-outline-secondary fw-bold px-3 me-2" onClick={() => {setEditingId(null); setForm(initialForm)}}>
                                        ANNULER
                                    </button>
                                )}
                                <button className={`btn btn-sm fw-bold px-4 py-2 text-white ${editingId ? 'btn-warning' : 'btn-success'}`} type="submit" style={!editingId ? { backgroundColor: '#1A7A38', borderColor: '#1A7A38' } : {}}>
                                    {editingId ? <><FaSave className="me-2"/> ENREGISTRER MODIFICATIONS</> : <><FaUserPlus className="me-2"/> CRÉER LE CLIENT</>}
                                </button>
                            </div>
                        </div>
                    </form>
                </div>
            </div>

            {/* TABLEAU SECTION */}
            <div className="card shadow-sm border-0 overflow-hidden">
                <div className="card-header bg-light p-3 d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-3 border-bottom">
                    <span className="fw-bold text-dark text-center text-md-start" style={{ letterSpacing: '1px' }}>
                        <FaUsers className="me-2 text-success"/> ANNUAIRE CLIENTS ({filteredClients.length})
                    </span>
                    <div className="input-group input-group-sm w-100 w-md-auto" style={{maxWidth: '300px'}}>
                        <span className="input-group-text bg-white text-muted border-end-0"><FaSearch /></span>
                        <input className="form-control border-start-0" placeholder="Rechercher code ou nom..." value={search} onChange={(e) => setSearch(e.target.value)} />
                    </div>
                </div>
                <div className="table-responsive" style={{ maxHeight: '500px' }}>
                    <table className="table table-sm table-hover align-middle mb-0 text-nowrap">
                        <thead className="bg-light sticky-top">
                            <tr className="text-uppercase text-muted" style={{ letterSpacing: '1px', fontSize: '0.85rem' }}>
                                <th className="ps-3 py-3 border-bottom-0">Code</th>
                                <th className="py-3 border-bottom-0" style={{minWidth: '200px'}}>Client / Structure</th>
                                <th className="py-3 border-bottom-0" style={{minWidth: '150px'}}>Contact / Email</th>
                                <th className="py-3 border-bottom-0">Identifiants Fiscaux</th>
                                <th className="py-3 text-center border-bottom-0">Solde Report</th>
                                <th className="pe-3 py-3 text-end border-bottom-0">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredClients.length === 0 && (
                                <tr><td colSpan="6" className="text-center py-4 text-muted">Aucun client trouvé.</td></tr>
                            )}
                            {filteredClients.map(c => (
                                <tr key={c._id} className={editingId === c._id ? 'table-active' : ''}>
                                    <td className="ps-3"><span className="badge bg-light text-dark border py-1 px-2">{c.code}</span></td>
                                    <td className="text-wrap" style={{minWidth: '200px'}}>
                                        <div className="fw-bold text-dark">{c.name}</div>
                                        <div className="small text-muted">{c.address}</div>
                                    </td>
                                    <td>
                                        <div className="text-dark">{c.contact}</div>
                                        <div className="small text-primary">{c.email || '-'}</div>
                                    </td>
                                    <td className="small text-muted">
                                        {c.ccNumber && <div>CC: <span className="text-dark">{c.ccNumber}</span></div>}
                                        {c.rccmNumber && <div>RCCM: <span className="text-dark">{c.rccmNumber}</span></div>}
                                    </td>
                                    <td className="text-center fw-bold text-danger">
                                        {c.initialDebt > 0 ? `${c.initialDebt.toLocaleString()} F` : '-'}
                                    </td>
                                    <td className="text-end pe-3">
                                        <button className="btn btn-sm btn-outline-warning rounded-1 me-1 py-1 px-2" onClick={() => handleDownloadRedevance(c._id, c.name)} title="État de Redevance">
                                            <FaFileInvoiceDollar size={14}/>
                                        </button>
                                        <button className="btn btn-sm btn-outline-primary rounded-1 me-1 py-1 px-2" onClick={() => startEdit(c)} title="Modifier">
                                            <FaEdit size={14}/>
                                        </button>
                                        {user?.role === 'admin' && (
                                            <button className="btn btn-sm btn-outline-danger rounded-1 py-1 px-2" onClick={() => handleDelete(c._id)} title="Supprimer">
                                                <FaTrash size={14}/>
                                            </button>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
};

export default ClientPage;